import { useEffect, useState, useCallback } from "react";
import api, { inr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChefHat, Check, RefreshCw } from "lucide-react";
import { toast } from "sonner";

export default function KOT() {
  const [orders, setOrders] = useState([]);

  const load = useCallback(async () => {
    const { data } = await api.get("/orders", { params: { status_filter: "open" } });
    // The kitchen board should only show today's still-pending tickets.
    // "open" also includes old orders that were never billed/closed, and
    // Acknowledge only flags kot_printed (the order can stay open for
    // billing) — so both need filtering out here, not just the status.
    const todayStr = new Date().toDateString();
    const pending = (data || []).filter(
      (o) => !o.kot_printed && new Date(o.created_at).toDateString() === todayStr,
    );
    setOrders(pending);
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [load]);

  const markServed = async (o) => {
    try {
      await api.post(`/orders/${o.id}/kot`);
      toast.success(`${o.order_no} acknowledged`);
      load();
    } catch (e) { toast.error("Failed"); }
  };

  return (
    <div className="p-6 sm:p-8" data-testid="kot-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading font-extrabold text-3xl text-[#2D2C2A] flex items-center gap-2">
            <ChefHat /> Kitchen Display
          </h1>
          <p className="text-sm text-[#7A7873] mt-1">Live KOT board · auto-refreshes every 10s.</p>
        </div>
        <Button variant="outline" onClick={load} data-testid="refresh-kot"><RefreshCw size={14} className="mr-1.5" /> Refresh</Button>
      </div>

      {orders.length === 0 ? (
        <div className="text-center py-20 text-[#7A7873]">
          <ChefHat className="mx-auto mb-2 opacity-40" size={48} />
          No open orders. Kitchen is calm.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {orders.map((o) => (
            <Card
              key={o.id}
              className="p-4 border-2 bg-white border-[#D95D39]"
              data-testid={`kot-${o.id}`}
            >
              <div className="flex items-baseline justify-between mb-2">
                <div>
                  <div className="font-heading font-extrabold text-lg text-[#2D2C2A]">{o.order_no}</div>
                  <div className="text-xs text-[#7A7873] capitalize">{o.type}{o.table_id ? " · Table" : ""}</div>
                </div>
                <div className="text-xs text-[#7A7873]">{new Date(o.created_at).toLocaleString("en-IN")}</div>
              </div>
              <div className="space-y-1.5 mb-3 border-t border-dashed border-[#EBE8E2] pt-2">
                {(o.items || []).map((it, i) => (
                  <div key={i} className="flex justify-between text-sm">
                    <span className="font-medium text-[#2D2C2A]">{it.name}{it.notes ? ` · ${it.notes}` : ""}</span>
                    <span className="font-mono-num font-bold">×{it.qty}</span>
                  </div>
                ))}
              </div>
              {o.notes && <div className="text-xs italic text-[#7A7873] mb-2">Note: {o.notes}</div>}
              <Button
                size="sm"
                className="w-full bg-[#6A7F66] hover:bg-[#586D54]"
                onClick={() => markServed(o)}
                data-testid={`ack-${o.id}`}
              >
                <Check size={14} className="mr-1.5" /> Acknowledge
              </Button>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
